<template>
  <div class="news-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="row justify-content-center text-center">
          <div class="col-lg-8">
            <div class="hero-content">
              <p class="hero-subtitle">NEWS</p>
              <h1 class="hero-title">Stay up to date</h1>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- News Articles Section -->
    <section class="news-articles-section py-5">
      <div class="container">

        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3 text-muted">Loading articles...</p>
        </div>

        <div v-else-if="news.length === 0" class="text-center py-5">
          <i class="bi bi-newspaper display-1 text-muted"></i>
          <h3 class="mt-3 text-muted">No articles found</h3>
          <p class="text-muted">{{ searchQuery ? 'Try adjusting your search terms.' : 'Check back later for new content.' }}</p>
        </div>

        <div v-else>
          <!-- Article Grid -->
          <div class="row">
            <div
              v-for="article in news"
              :key="article.id"
              class="col-lg-6 col-md-6 mb-4"
            >
              <article class="news-card h-100">
                <!-- Article Image - Show container only if image exists -->
                <div v-if="getFeaturedImage(article)" class="article-image-container">
                  <img
                    :src="getFeaturedImage(article)"
                    :alt="article.title"
                    class="article-image"
                    @error="handleImageError"
                    loading="lazy"
                  >
                  <div class="featured-badge" v-if="article.is_featured">
                    Featured
                  </div>
                </div>

                <!-- Article Content -->
                <div class="article-content" :class="{ 'no-image': !getFeaturedImage(article) }">
                  <div class="article-meta">
                    <span class="article-date">{{ formatDate(article.date) }}</span>
                    <span class="article-author" v-if="article.author">
                      by {{ article.author.name }}
                    </span>
                  </div>

                  <h3 class="article-title">{{ article.title }}</h3>

                  <p class="article-excerpt">{{ truncateContent(article.content, 120) }}</p>

                  <button class="btn btn-primary read-more-btn" @click="readMore(article)">
                    Read more <i class="bi bi-arrow-right ms-2"></i>
                  </button>
                </div>
              </article>
            </div>
          </div>

          <!-- Pagination -->
          <div class="row mt-5" v-if="pagination.total_pages > 1">
            <div class="col-12">
              <nav aria-label="News pagination">
                <ul class="pagination justify-content-center">
                  <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                    <button
                      class="page-link"
                      @click="goToPage(pagination.current_page - 1)"
                      :disabled="pagination.current_page === 1"
                    >
                      <i class="bi bi-chevron-left"></i>
                    </button>
                  </li>

                  <li
                    v-for="page in paginationPages"
                    :key="page"
                    class="page-item"
                    :class="{ active: page === pagination.current_page }"
                  >
                    <button class="page-link" @click="goToPage(page)">{{ page }}</button>
                  </li>

                  <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
                    <button
                      class="page-link"
                      @click="goToPage(pagination.current_page + 1)"
                      :disabled="pagination.current_page === pagination.total_pages"
                    >
                      <i class="bi bi-chevron-right"></i>
                    </button>
                  </li>
                </ul>

                <!-- Results Counter -->
                <div class="pagination-info text-center mt-3">
                  <small class="text-muted">
                    Showing {{ ((pagination.current_page - 1) * pagination.per_page) + 1 }} to {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }} of {{ pagination.total }} results
                  </small>
                </div>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- News Detail Modal -->
    <div class="modal fade" id="newsModal" tabindex="-1" ref="newsModal">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div v-if="selectedArticle" class="modal-header">
            <h2 class="modal-title mb-0">{{ selectedArticle.title }}</h2>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body p-0">
            <div v-if="selectedArticle">
              <!-- Featured Image in Modal - Only show if image exists -->
              <div v-if="getFeaturedImage(selectedArticle)" class="modal-featured-image">
                <img
                  :src="getFeaturedImage(selectedArticle)"
                  :alt="selectedArticle.title"
                  class="modal-image-full"
                  @error="handleImageError"
                >
              </div>

              <!-- Article Content with Padding -->
              <div class="modal-article-content">
                <div class="article-meta-modal mb-3">
                  <span class="article-date-modal">{{ formatDate(selectedArticle.date) }}</span>
                  <span class="article-author-modal" v-if="selectedArticle.author">
                    • by {{ selectedArticle.author.name }}
                  </span>
                </div>

                <div class="modal-content-text rich-content" v-html="formatContent(selectedArticle.content)"></div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NewsApiService from '@/services/news/NewsApiServices'

export default {
  name: 'PublicNews',

  data() {
    return {
      news: [],
      loading: false,
      searchQuery: '',
      selectedArticle: null,
      pagination: {
        current_page: 1,
        total_pages: 1,
        per_page: 10,
        total: 0
      },
      error: null,
      newsModalInstance: null,
    }
  },

  computed: {
    paginationPages() {
      const pages = []
      const current = this.pagination.current_page
      const total = this.pagination.total_pages

      let start = Math.max(1, current - 2)
      let end = Math.min(total, start + 4)

      if (end - start < 4) {
        start = Math.max(1, end - 4)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      return pages
    },

    paginatedNews() {
      return this.news;
    }
  },

  async mounted() {
    await this.fetchNews(1)
    await this.openArticleFromQuery()
  },

  methods: {
    async fetchNews(page = 1) {
      this.loading = true;

      const res = await NewsApiService.getPublicNews({
        page,
        per_page: this.pagination.per_page,
        search: this.searchQuery || ''
      });

      this.news = res.data;
      this.pagination = res.pagination;

      this.loading = false;
    },

    // Homepage "Read more" links here with ?openArticle=<id> so the specific
    // article opens directly, instead of landing on the plain news list.
    async openArticleFromQuery() {
      const articleId = this.$route.query.openArticle
      if (!articleId) return

      const fromCurrentPage = this.news.find(
        (article) => String(article.id) === String(articleId)
      )

      if (fromCurrentPage) {
        this.readMore(fromCurrentPage)
        return
      }

      // Not on the first page (older article) - fetch it directly.
      try {
        const article = await NewsApiService.getPublicNewsById(articleId)
        if (article) {
          this.readMore(article)
        }
      } catch (error) {
        console.error('Failed to open article from query:', error)
      }
    },

    async searchNews() {
      await this.fetchNews(1)
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.total_pages) return;

      this.pagination.current_page = page;
      this.fetchNews(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    readMore(article) {
      this.selectedArticle = article

      const modalElement = this.$refs.newsModal
      if (!modalElement) return

      // Bootstrap 5
      if (window.bootstrap?.Modal) {
        this.newsModalInstance = window.bootstrap.Modal.getOrCreateInstance(modalElement)
        this.newsModalInstance.show()
      }
      // Bootstrap 4 fallback
      else if (window.$?.fn?.modal) {
        window.$(modalElement).modal('show')
      }
      // Manual fallback
      else {
        modalElement.style.display = 'block'
        modalElement.classList.add('show')
        document.body.classList.add('modal-open')
        document.body.style.overflow = 'hidden'

        const backdrop = document.createElement('div')
        backdrop.className = 'modal-backdrop fade show'
        backdrop.onclick = () => this.closeModal()
        document.body.appendChild(backdrop)
      }
    },

    closeModal() {
      const modalElement = this.$refs.newsModal
      if (!modalElement) return

      // Bootstrap 5
      if (this.newsModalInstance) {
        this.newsModalInstance.hide()
        this.newsModalInstance = null
      }
      // Bootstrap 4 fallback
      else if (window.$?.fn?.modal) {
        window.$(modalElement).modal('hide')
      }
      // Manual fallback
      else {
        modalElement.style.display = 'none'
        modalElement.classList.remove('show')
      }

      // 🔑 ALWAYS restore scroll
      document.body.classList.remove('modal-open')
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''

      // Remove leftover backdrop(s)
      document.querySelectorAll('.modal-backdrop').forEach(b => b.remove())

      this.selectedArticle = null
    },

    getFeaturedImage(article) {
      if (!article) return null
      const imageUrl = article.featured_image_url

      if (!imageUrl) {
        return null
      }

      // If it's already a full URL with http/https, use as-is
      if (imageUrl.startsWith('http')) {
        return imageUrl
      }

      // CRITICAL FIX: Use the correct API server port (8000) not frontend port (8080)
      const API_BASE_URL = 'http://localhost:8000' // Your Laravel API server

      // If it starts with /storage, build URL with correct API server
      if (imageUrl.startsWith('/storage/')) {
        const fullUrl = `${API_BASE_URL}${imageUrl}`
        return fullUrl
      }

      // Fallback: assume it needs /storage/ prefix with API server
      const fallbackUrl = `${API_BASE_URL}/storage/${imageUrl}`
      return fallbackUrl
    },

    handleImageError(event) {
      console.warn('Image failed to load:', event.target.src)
      // Hide the image container when image fails to load
      const imageContainer = event.target.closest('.article-image-container, .modal-featured-image')
      if (imageContainer) {
        imageContainer.style.display = 'none'
      }
    },

    formatDate(dateString) {
      return NewsApiService.formatNewsDate(dateString)
    },

    truncateContent(content, maxLength = 150) {
      return NewsApiService.truncateContent(content, maxLength)
    },

    formatContent(content) {
      if (!content) return ''

      // Content authored with the rich-text editor is already valid HTML
      // (e.g. real <ul><li> markup). Running that through the legacy
      // newline-to-<p>/<br> conversion below would double-wrap it in <p>
      // tags and mangle the markup, so render it as-is. Only legacy
      // plain-text articles (no HTML tags) need the newline conversion.
      const containsHtml = /<[a-z][\s\S]*>/i.test(content)
      if (containsHtml) {
        return content
      }

      // Convert line breaks to HTML and handle paragraph formatting
      let formattedContent = content.replace(/\n\n/g, '</p><p>')
      formattedContent = formattedContent.replace(/\n/g, '<br>')

      if (!formattedContent.startsWith('<p>')) {
        formattedContent = '<p>' + formattedContent + '</p>'
      }

      return formattedContent
    },

    clearSearch() {
      this.searchQuery = ''
      this.fetchNews(1)
    }
  }
}
</script>

<style scoped>
/* Hero Section */
.hero-section {
  color: white;
  padding: 6rem 0 4rem;
  text-align: center;
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: var(--font-size-4xl);
  font-weight: var(--font-weight-bold);
  margin: 0 0 1rem 0;
  line-height: var(--line-height-tight);
}

/* News Articles Section */
.news-articles-section {
  background-color: #f8f9fa;
  min-height: 60vh;
}

/* News Card Layout */
.news-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.news-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(26, 128, 144, 0.15);
}

/* Article Image Container */
.article-image-container {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #f8f9fa;
}

.article-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.news-card:hover .article-image {
  transform: scale(1.05);
}

.featured-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  background: #1a8090;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Article Content */
.article-content {
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* When there's no image, give more space to content */
.article-content.no-image {
  padding: 2rem 1.5rem;
}

.article-meta {
  margin-bottom: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: var(--font-size-sm);
}

.article-date {
  color: #6c757d;
  font-weight: var(--font-weight-medium);
}

.article-author {
  color: #1a8090;
  font-weight: var(--font-weight-medium);
}

.article-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: #2c3e50;
  margin-bottom: 1rem;
  line-height: var(--line-height-normal);
  flex-shrink: 0;
}

.article-excerpt {
  color: #6c757d;
  line-height: var(--line-height-relaxed);
  margin-bottom: 1.5rem;
  flex: 1;
  font-size: var(--font-size-sm);
}

.read-more-btn {
  align-self: flex-start;
  margin-top: auto;
}

/* Button Styles */
.btn-primary {
  background: linear-gradient(135deg, #1a8090 0%, #0d5d6b 100%);
  border: none;
  padding: 0.75rem 1.5rem;
  font-weight: var(--font-weight-semibold);
  border-radius: 25px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(26, 128, 144, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(26, 128, 144, 0.4);
  background: linear-gradient(135deg, #0d5d6b 0%, #1a8090 100%);
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
  padding: 0.75rem 1.5rem;
  font-weight: var(--font-weight-semibold);
  border-radius: 25px;
}

/* Pagination */
.pagination {
  margin-bottom: 1rem;
}

.pagination .page-link {
  color: #1a8090;
  border: 2px solid #e9ecef;
  padding: 0.75rem 1rem;
  margin: 0 0.25rem;
  border-radius: 8px;
  font-weight: var(--font-weight-medium);
  transition: all 0.3s ease;
}

.pagination .page-item.active .page-link {
  background-color: #1a8090;
  border-color: #1a8090;
  color: white;
}

.pagination .page-link:hover:not(.disabled) {
  color: #0d5d6b;
  background-color: rgba(26, 128, 144, 0.1);
  border-color: #1a8090;
  transform: translateY(-1px);
}

.pagination-info {
  color: #6c757d;
  font-size: var(--font-size-sm);
}

/* Modal Styles */
.modal-content {
  border-radius: 15px;
  border: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #1a8090 0%, #0d5d6b 100%);
  color: white;
  border-radius: 15px 15px 0 0;
  padding: 1.5rem;
  border-bottom: none;
}

.modal-title {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-2xl);
  margin: 0;
}

.btn-close {
  filter: brightness(0) invert(1);
  background: none;
  border: none;
  color: white;
  font-size: var(--font-size-xl);
  cursor: pointer;
  padding: 0.5rem;
  opacity: 0.8;
  transition: opacity 0.3s ease;
}

.btn-close:hover {
  opacity: 1;
}

/* Modal Featured Image - Full Width */
.modal-featured-image {
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #f8f9fa;
  position: relative;
}

.modal-image-full {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Modal Article Content */
.modal-article-content {
  padding: 2rem;
}

.article-meta-modal {
  color: #6c757d;
  font-size: var(--font-size-sm);
  margin-bottom: 1rem;
}

.article-date-modal {
  font-weight: var(--font-weight-medium);
}

.article-author-modal {
  color: #1a8090;
  font-weight: var(--font-weight-medium);
}

.modal-article-title {
  color: #2c3e50;
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-normal);
  margin-bottom: 1.5rem;
}

.modal-content-text {
  color: #495057;
  font-size: var(--font-size-md);
  line-height: var(--line-height-relaxed);
}

.modal-content-text p {
  margin-bottom: 1.5rem;
}

.modal-content-text p:last-child {
  margin-bottom: 0;
}

.modal-footer {
  background-color: #f8f9fa;
  border-top: 1px solid #dee2e6;
  border-radius: 0 0 15px 15px;
  padding: 1rem 2rem;
}

/* Loading States */
.spinner-border {
  color: #1a8090;
}

/* Responsive Design */
@media (max-width: 992px) {
  .hero-title {
    font-size: var(--font-size-4xl);
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: var(--font-size-3xl);
  }

  .hero-section {
    padding: 4rem 0 3rem;
  }

  .article-content {
    padding: 1.25rem;
  }

  .article-content.no-image {
    padding: 1.5rem;
  }

  .article-title {
    font-size: var(--font-size-lg);
  }

  /* Modal responsive adjustments */
  .modal-article-content {
    padding: 1.5rem;
  }

  .modal-article-title {
    font-size: var(--font-size-xl);
  }

  .modal-content-text {
    font-size: var(--font-size-base);
  }
}

@media (max-width: 576px) {
  .hero-title {
    font-size: var(--font-size-2xl);
  }

  .hero-section {
    padding: 3rem 0 2rem;
  }

  .article-content {
    padding: 1rem;
  }

  .article-content.no-image {
    padding: 1.25rem;
  }

  .article-title {
    font-size: var(--font-size-md);
  }

  .col-lg-6 {
    margin-bottom: 2rem;
  }

  /* Modal mobile adjustments */
  .modal-article-content {
    padding: 1rem;
  }

  .modal-article-title {
    font-size: var(--font-size-xl);
  }
}

/* Animation */
.news-card {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Card hover effects */
.news-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(26, 128, 144, 0.05) 0%, rgba(13, 93, 107, 0.05) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  border-radius: 12px;
}

.news-card:hover::after {
  opacity: 1;
}

/* Empty state */
.text-center i.display-1 {
  color: #dee2e6;
}
</style>
